import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MessageCircle, Loader2 } from "lucide-react";
import { BRAND } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuoteDialog({ trigger, defaultProduct = "" }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    company_name: "",
    email: "",
    phone: "",
    country: "",
    product_required: defaultProduct,
    quantity: "",
    message: "",
  });

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Inquiry received. Our B2B team will contact you within 24 hours.");
      setOpen(false);
      setForm({
        name: "", company_name: "", email: "", phone: "",
        country: "", product_required: "", quantity: "", message: "",
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  const waMsg = encodeURIComponent(
    `Hello Desi Masala Hub,\nI'd like a bulk quote.\nProduct: ${form.product_required || "—"}\nQuantity: ${form.quantity || "—"}\nName: ${form.name || "—"}\nCompany: ${form.company_name || "—"}`
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        data-testid="quote-dialog"
        className="max-w-2xl bg-[#F7F4ED] border-[#6B3E26]/15 max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-serif-display text-3xl text-[#6B3E26] font-semibold">
            Request a Bulk Quote
          </DialogTitle>
          <DialogDescription className="text-[#4A4A4A]">
            Share your requirement — our B2B team will revert within 24 business hours with pricing & samples.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="q-name">Name *</Label>
            <Input id="q-name" data-testid="quote-name" value={form.name}
              onChange={(e) => update("name", e.target.value)} required
              className="bg-white border-[#6B3E26]/20" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="q-company">Company Name</Label>
            <Input id="q-company" data-testid="quote-company" value={form.company_name}
              onChange={(e) => update("company_name", e.target.value)}
              className="bg-white border-[#6B3E26]/20" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="q-email">Email *</Label>
            <Input id="q-email" type="email" data-testid="quote-email" value={form.email}
              onChange={(e) => update("email", e.target.value)} required
              className="bg-white border-[#6B3E26]/20" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="q-phone">Phone *</Label>
            <Input id="q-phone" data-testid="quote-phone" value={form.phone}
              onChange={(e) => update("phone", e.target.value)} required
              className="bg-white border-[#6B3E26]/20" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="q-country">Country</Label>
            <Input id="q-country" data-testid="quote-country" value={form.country}
              onChange={(e) => update("country", e.target.value)}
              className="bg-white border-[#6B3E26]/20" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="q-product">Product Required</Label>
            <Input id="q-product" data-testid="quote-product" value={form.product_required}
              onChange={(e) => update("product_required", e.target.value)}
              placeholder="e.g. Turmeric Powder"
              className="bg-white border-[#6B3E26]/20" />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="q-qty">Quantity (kg / MT)</Label>
            <Input id="q-qty" data-testid="quote-quantity" value={form.quantity}
              onChange={(e) => update("quantity", e.target.value)}
              placeholder="e.g. 500 kg / 1 MT"
              className="bg-white border-[#6B3E26]/20" />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="q-msg">Message</Label>
            <Textarea id="q-msg" data-testid="quote-message" value={form.message}
              onChange={(e) => update("message", e.target.value)} rows={3}
              placeholder="Specifications, packaging, target market…"
              className="bg-white border-[#6B3E26]/20" />
          </div>

          <DialogFooter className="md:col-span-2 flex-col sm:flex-row gap-2 pt-2">
            <a
              href={`https://wa.me/${BRAND.whatsapp}?text=${waMsg}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="quote-whatsapp-button"
              className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1DA851] text-white px-6 py-3 rounded-sm text-sm font-semibold tracking-wide transition-colors"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp Inquiry
            </a>
            <Button
              type="submit"
              data-testid="quote-submit-button"
              disabled={loading}
              className="bg-[#C38B2A] hover:bg-[#A37423] text-white px-8 py-3 rounded-sm text-sm tracking-wide"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Request Quote
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
